// Sky dome and outdoor image-based lighting.
//
// The scene background was a flat `0x121a26` fill with linear fog matched to it, and the PBR
// environment came from three's RoomEnvironment — a synthetic photo-studio box. Correct for
// material response, but it is an *indoor* probe, which is why materials read slightly
// showroom-lit and why the horizon had no depth.
//
// Both are replaced here with procedural geometry/shaders. No texture assets: the existing
// makeGroundTexture already establishes runtime canvas/procedural generation as the idiom.

import * as THREE from 'three';

/** Zenith / horizon / ground band. Shared by the visible dome and the IBL probe so they agree. */
export interface SkyPalette {
  zenith: THREE.Color;
  horizon: THREE.Color;
  ground: THREE.Color;
  sun: THREE.Color;
}

export const DEFAULT_SKY: SkyPalette = {
  // TEST — a late-afternoon overcast-break palette that keeps the existing cool-shadow/warm-key
  // balance the faction colours were authored against.
  zenith: new THREE.Color(0x2c4468),
  horizon: new THREE.Color(0x8a92a3),
  ground: new THREE.Color(0x2a2418),
  sun: new THREE.Color(0xffd9a8),
};

const SKY_VERTEX = /* glsl */ `
  varying vec3 vWorldDirection;
  void main() {
    // Direction from the camera to this vertex, in world space — the dome is centred on the
    // camera each frame, so this is a stable view ray.
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vWorldDirection = normalize(worldPosition.xyz - cameraPosition);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const SKY_FRAGMENT = /* glsl */ `
  uniform vec3 zenith;
  uniform vec3 horizon;
  uniform vec3 groundColor;
  uniform vec3 sunColor;
  uniform vec3 sunDirection;
  varying vec3 vWorldDirection;

  void main() {
    vec3 dir = normalize(vWorldDirection);

    // Tighten the gradient near the horizon so the band reads as atmosphere rather than a
    // linear ramp across the whole dome.
    float up = dir.y;
    float skyBlend = pow(clamp(up, 0.0, 1.0), 0.42);
    vec3 color = mix(horizon, zenith, skyBlend);

    // Below the horizon the dome is only ever seen past the map rim; keep it dark so it reads
    // as distant terrain haze instead of a visible seam.
    float belowBlend = smoothstep(0.0, -0.16, up);
    color = mix(color, groundColor, belowBlend);

    // Sun disc plus a wide halo. The disc is deliberately soft — a hard edge reads as a decal.
    float sunAmount = max(dot(dir, normalize(sunDirection)), 0.0);
    float disc = smoothstep(0.9985, 0.9997, sunAmount);
    float halo = pow(sunAmount, 88.0) * 0.55 + pow(sunAmount, 8.0) * 0.12;
    color += sunColor * (disc * 2.2 + halo);

    gl_FragColor = vec4(color, 1.0);
  }
`;

export interface SkyDome {
  mesh: THREE.Mesh;
  /** Re-centre on the camera and re-aim the sun. Call once per frame. */
  update(cameraPosition: THREE.Vector3, sunPosition: THREE.Vector3, sunTarget: THREE.Vector3): void;
  dispose(): void;
}

/**
 * A back-faced sphere with a gradient shader. Radius sits inside the camera's far plane (700)
 * and outside the fog far distance so the dome itself is never fogged.
 */
export function makeSkyDome(palette: SkyPalette = DEFAULT_SKY): SkyDome {
  const material = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    // The dome must not be fogged — fog is what distant *geometry* dissolves into, and the sky
    // is the thing it dissolves into.
    fog: false,
    // Rendered into the HDR composer target; OutputPass tone-maps it with everything else.
    toneMapped: true,
    uniforms: {
      zenith: { value: palette.zenith.clone() },
      horizon: { value: palette.horizon.clone() },
      groundColor: { value: palette.ground.clone() },
      sunColor: { value: palette.sun.clone() },
      sunDirection: { value: new THREE.Vector3(0.5, 0.6, 0.4).normalize() },
    },
    vertexShader: SKY_VERTEX,
    fragmentShader: SKY_FRAGMENT,
  });

  const geometry = new THREE.SphereGeometry(480, 32, 16);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  // Draw first, and never let it write depth over the world.
  mesh.renderOrder = -1000;

  const sunDir = new THREE.Vector3();
  return {
    mesh,
    update(cameraPosition, sunPosition, sunTarget) {
      // Keeping the dome centred on the camera means it can never be escaped or clipped.
      mesh.position.copy(cameraPosition);
      sunDir.copy(sunPosition).sub(sunTarget).normalize();
      (material.uniforms.sunDirection.value as THREE.Vector3).copy(sunDir);
    },
    dispose() {
      geometry.dispose();
      material.dispose();
    },
  };
}

/**
 * Outdoor IBL probe: a sky-coloured gradient box with a ground plane, prefiltered through PMREM.
 *
 * Replaces RoomEnvironment. Cheap to build (a handful of emissive faces, generated once), and it
 * gives materials a sky-above / ground-below ambient response instead of a studio box.
 */
export function makeSkyEnvironment(
  renderer: THREE.WebGLRenderer,
  palette: SkyPalette = DEFAULT_SKY,
): THREE.Texture {
  const scene = new THREE.Scene();

  const emissive = (color: THREE.Color, intensity: number) =>
    new THREE.MeshBasicMaterial({ color: color.clone().multiplyScalar(intensity), side: THREE.BackSide });

  // Upper hemisphere: zenith at the top face, horizon around the sides.
  const box = new THREE.BoxGeometry(100, 100, 100);
  const shell = new THREE.Mesh(box, [
    emissive(palette.horizon, 1),
    emissive(palette.horizon, 1),
    emissive(palette.zenith, 1.15),
    emissive(palette.ground, 0.85),
    emissive(palette.horizon, 1),
    emissive(palette.horizon, 1),
  ]);
  scene.add(shell);

  // A bright patch standing in for the sun, so specular highlights have something to catch.
  const sunPatch = new THREE.Mesh(
    new THREE.PlaneGeometry(26, 26),
    new THREE.MeshBasicMaterial({ color: palette.sun.clone().multiplyScalar(5.5) }),
  );
  sunPatch.position.set(22, 40, 18);
  sunPatch.lookAt(0, 0, 0);
  scene.add(sunPatch);

  const pmrem = new THREE.PMREMGenerator(renderer);
  const texture = pmrem.fromScene(scene, 0.06).texture;
  pmrem.dispose();

  box.dispose();
  shell.material.forEach((material) => material.dispose());
  sunPatch.geometry.dispose();
  (sunPatch.material as THREE.Material).dispose();

  return texture;
}
